import {
  type CreateAdminSocietyDto,
  type UpdateAdminSocietyDto,
  type V2AdminSocietiesPageDto,
  type V2AdminSocietiesSummaryDto,
  type V2AdminSocietyDto,
  v2AdminSocietiesControllerCreate,
  v2AdminSocietiesControllerDetail,
  v2AdminSocietiesControllerList,
  v2AdminSocietiesControllerLookup,
  v2AdminSocietiesControllerRemove,
  v2AdminSocietiesControllerSummary,
  v2AdminSocietiesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminSocietiesPageSchema,
  V2AdminSocietiesSummarySchema,
  V2AdminSocietyLookupSchema,
  V2AdminSocietySchema
} from '@idc-forms/schemas/api-v2/admin-societies'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/** Entrada del selector de sociedades (`soc_` + nombre) para los forms que cascadean. */
/**
 * `company` es el `cmp_` de la empresa madre, y viaja porque el selector de negociaciones
 * CASCADEA por ella: elegida la empresa, sólo se ofrecen sus sociedades. La API ya lo
 * devolvía; el mapper lo descartaba.
 */
export type SocietyV2LookupOption = { id: string; name: string; company: string }

/**
 * Administración de sociedades contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público (`soc_`); la empresa
 * madre viaja por `cmp_` y la autoría por `usr_`, nunca como ObjectId. Las
 * credenciales SAP nunca vuelven — sólo el flag `sapConfigured`.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer`
 * con bracket notation. El serializer por defecto del SDK usa `deepObject` de UN
 * solo nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type SocietiesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type SocietiesAdminV2Row = V2AdminSocietiesPageDto['data'][number]

function toListQuery(query: SocietiesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const societiesAdminV2Service = {
  lookup: async (accessToken: string): Promise<SocietyV2LookupOption[]> => {
    const result = await v2AdminSocietiesControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminSocietyLookupSchema, result.data).data.map((society) => ({
      id: society.id,
      name: society.name,
      company: society.company
    }))
  },

  list: async (
    accessToken: string,
    query: SocietiesAdminV2Query
  ): Promise<V2AdminSocietiesPageDto> => {
    const result = await v2AdminSocietiesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminSocietiesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<SocietiesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminSocietiesSummaryDto> => {
    const result = await v2AdminSocietiesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminSocietiesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminSocietyDto> => {
    const result = await v2AdminSocietiesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminSocietySchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminSocietyDto): Promise<V2AdminSocietyDto> => {
    const result = await v2AdminSocietiesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminSocietyDto
  ): Promise<V2AdminSocietyDto> => {
    const result = await v2AdminSocietiesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminSocietiesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
